/**
 * OrderChangeIssuesList — tab content for the Order Changes issues tab (BLU-695).
 *
 * NO LONGER READ ONLY. Phase 3 turned this tab into the resolution surface for
 * a drifted order: the browse list reports what changed, and each card opens
 * `OrderChangeResolution`, which reads LIVE coverage and applies the fix.
 *
 * GROUPED BY ORDER, not by SKU (hand-over locked decision 3). Both fan-in
 * mutations take a `prod_order_id`, never an issue id, so a per-SKU-row button
 * would silently heal its siblings and report a number that does not match what
 * happened. One card = one order = one mutation target; the SKU rows live
 * inside the resolution plan, which is computed server-side.
 *
 * Where the resolution renders is a count question (locked decision 4):
 *   1 to 2 changed base SKUs -> the card expands IN PLACE (~85% of changes)
 *   3 or more                -> `OrderChangeSheet`, the right-hand panel
 * Both mount the same `OrderChangeResolution`; see `resolutionPresentation`.
 *
 * DESIGN LANE: inventory-app density for BROWSING, operator lane for ACTING.
 * Browse rows keep `text-sm` headings, `Card`/`CardContent py-4`, `text-xs`
 * metadata and `formatDistanceToNow` timestamps, matching the sibling
 * `SplitOrderIssuesList`. Every ACTION control is `h-12`, matching
 * `OrderResolutionFlow` (the other coordinator action lane on this board) and
 * the shared `IssueSearchInput`. Two lanes, deliberately; do not invent a third.
 *
 * Each card answers three questions at a glance:
 *   1. WHICH order + WHICH base garment SKUs drifted (`blocked_sku` is the
 *      normalized BASE sku, e.g. TEE-S-BK — never a design-bearing shopify_sku,
 *      hence the explicit "Base SKU" label).
 *   2. WHAT changed — the union of `metadata.changeKinds`, as neutral outline
 *      badges.
 *   3. BY HOW MUCH — "Needs N more" (amber + up glyph) and/or "N surplus"
 *      (blue + down glyph). Colour is never the only signal: each carries a
 *      glyph AND its own words (DESIGN.md §16 / PRODUCTION_DESIGN.md §13).
 *      These browse counts come from the stored `prod_issues.metadata` snapshot
 *      and can be up to a cron cycle stale — which is exactly why the
 *      resolution card re-reads live coverage instead of trusting them.
 */
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ExternalLink,
  FileEdit,
  Loader2,
  MinusCircle,
  PanelRightOpen,
  PlusCircle,
  X,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import { EmptyState } from '@/components/shared/EmptyState';
import { useClientOrgLabels } from '@/hooks/useClientOrgLabels';
import { useRole } from '@/hooks/use-role';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAcceptOrderChanges, useIssueCounts, useProductionIssues } from '../hooks/useProductionIssues';
import { IssueSearchInput } from './IssueSearchInput';
import { IssuesErrorBoundary } from './IssuesErrorBoundary';
import { OrderChangeResolution, resolutionPresentation, type BinMoveResult } from './OrderChangeResolution';
import { OrderChangeSheet } from './OrderChangeSheet';
import {
  changeSentence,
  garmentLabel,
  inPlanOrder,
  type Translate,
  type WordingLine,
} from '../orderChangeWording';
import type { ProductionIssue } from '../types';

/** Rows fetched per SERVER page. With months of backfill a location can hold
 *  hundreds of open ORDER_CHANGED rows; the old hardcoded single fetch of 50
 *  made everything older permanently unreachable. Offset is now wired to the
 *  pager (PrintFileIssuesList pattern), so every row is reachable. Grouping is
 *  per-page: an order's rows can straddle a page boundary and show as two
 *  partial cards — the resolver always re-reads live coverage, so actions stay
 *  correct; a grouped browse RPC is the future fix. */
const FETCH_LIMIT = 50;
/** Base SKUs, or changed lines, shown on the card before collapsing to
 *  "+N more". */
const MAX_SKUS_IN_HEADER = 3;

/**
 * Mirror of the backend `OrderChangeKindSchema`
 * (apps/backend/src/modules/production-issues/production-issues.types.ts).
 * Declared locally because `../types.ts` is owned elsewhere in this change; its
 * natural home is beside `IssueType` there.
 */
export type OrderChangeKind =
  | 'line_added'
  | 'line_removed'
  | 'qty_increased'
  | 'qty_decreased'
  | 'ships_elsewhere'
  | 'swap'
  | 'order_cancelled';

const ORDER_CHANGE_KINDS: readonly OrderChangeKind[] = [
  'line_added',
  'line_removed',
  'qty_increased',
  'qty_decreased',
  'ships_elsewhere',
  'swap',
  'order_cancelled',
] as const;

/** The slice of `prod_issues.metadata` this surface reads, after validation. */
interface OrderChangeView {
  changeKinds: OrderChangeKind[];
  under: number;
  over: number;
  /** ISO string, or null when the writer left it off / it is unparseable. */
  evaluatedAt: string | null;
  /** The row's changed order lines; empty when it carries no per-line data. */
  changedLines: OrderChangeLine[];
}

/** One changed order line of a row, ready for `changeSentence`. */
export interface OrderChangeLine extends WordingLine {
  /** Stable React key: the issue id plus the line's position in the row. */
  key: string;
}

function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

/**
 * `blocked_sku` of an ORDER_CHANGED row whose changed line carries no SKU.
 * ⚠ KEEP IN LOCKSTEP with `groupBySku` in
 * apps/backend/src/modules/production-orders/order-change-issues.ts.
 */
const UNKNOWN_SKU_ISSUE_KEY = '(unknown)';

/** Only render a date we can actually format — `new Date(undefined)` throws in
 *  formatDistanceToNow, which would take the whole tab down. */
function toIsoOrNull(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  return Number.isNaN(new Date(value).getTime()) ? null : value;
}

/**
 * The changed lines of one row, from `metadata.lines` (written by the detector
 * as `{ orderItemId, expected, active, under, over }`, plus
 * `shipsFromAnotherLocation: true` on a line Shopify now ships from another
 * location). Untyped JSON, so every
 * field is checked: anything that is not a line, and any line with no shortfall
 * and no surplus, is left out. The garment name comes from
 * `order_change_line_display` by line id; the SKU is the row's own base SKU.
 * A row with no readable changed line returns [] and renders as it always did.
 */
function readChangedLines(issue: ProductionIssue): OrderChangeLine[] {
  const rawLines = issue.metadata?.lines;
  if (!Array.isArray(rawLines)) return [];

  const names = Array.isArray(issue.order_change_line_display) ? issue.order_change_line_display : [];
  const out: OrderChangeLine[] = [];
  rawLines.forEach((entry: unknown, index: number) => {
    if (!entry || typeof entry !== 'object') return;
    const raw = entry as Record<string, unknown>;
    const under = toCount(raw.under);
    const over = toCount(raw.over);
    if (under === 0 && over === 0) return;
    const orderItemId = typeof raw.orderItemId === 'string' ? raw.orderItemId : null;
    const display = orderItemId ? names.find((n) => n?.orderItemId === orderItemId) : undefined;
    out.push({
      key: `${issue.id}:${index}`,
      // The detector files a changed line that has no SKU under the literal key
      // '(unknown)' (backend `groupBySku`). That is a grouping key, not a SKU:
      // passing it on would print "(unknown) removed" here while the plan for
      // the same line says "Unknown SKU". Dropped, so the sentence falls back to
      // the same `planUnknownSku` copy the plan uses.
      sku: issue.blocked_sku === UNKNOWN_SKU_ISSUE_KEY ? null : issue.blocked_sku,
      expected: toCount(raw.expected),
      active: toCount(raw.active),
      under,
      over,
      // BLU-869: the detector writes this key (true) only on a split order's
      // line that Shopify now ships from another location.
      shipsFromAnotherLocation: raw.shipsFromAnotherLocation === true,
      // Written beside the flag: what the customer wants now, for "1 of 2".
      ...(raw.shipsFromAnotherLocation === true && typeof raw.customerQuantity === 'number'
        ? { customerQuantity: raw.customerQuantity }
        : {}),
      display: display ?? null,
    });
  });
  return out;
}

/**
 * Read the order-change metadata defensively. `metadata` is untyped JSON from
 * the DB, so an older/partial row must degrade to an honest row rather than a
 * crash: unknown kinds are dropped, `under` falls back to the row's `deficit`,
 * and a missing `evaluatedAt` falls back to the row's own timestamps.
 */
function readOrderChangeView(issue: ProductionIssue): OrderChangeView {
  const metadata = issue.metadata ?? {};
  const rawKinds = Array.isArray(metadata.changeKinds) ? metadata.changeKinds : [];
  const changeKinds = ORDER_CHANGE_KINDS.filter((kind) => rawKinds.includes(kind));
  const under = toCount(metadata.under) || toCount(issue.deficit);
  return {
    changeKinds,
    under,
    over: toCount(metadata.over),
    evaluatedAt:
      toIsoOrNull(metadata.evaluatedAt) ??
      toIsoOrNull(issue.updated_at) ??
      toIsoOrNull(issue.created_at),
    changedLines: readChangedLines(issue),
  };
}

// ============================================================================
// Grouping — one card per prod_order (locked decision 3)
// ============================================================================

export interface OrderChangeGroup {
  /** React key + identity. `prod_order_id`, or `issue:<id>` when it is null. */
  key: string;
  /** Null when the writer left no prod_order link — then nothing is resolvable. */
  prodOrderId: string | null;
  /** Every ORDER_CHANGED row for this order, in the order the list returned them. */
  issues: ProductionIssue[];
  /** The rows "Accept as-is" would resolve. Resolved rows are excluded. */
  openIssueIds: string[];
  /** Union of every row's kinds, in the canonical ORDER_CHANGE_KINDS order. */
  changeKinds: OrderChangeKind[];
  /** Snapshot totals across the group (browse only — the resolver reads live). */
  under: number;
  over: number;
  /** Most recent evaluation across the group. */
  evaluatedAt: string | null;
  /** True only when NO row is still open. */
  allResolved: boolean;
  /** Distinct base SKUs, first-seen order. Drives the inline-vs-Sheet choice. */
  baseSkus: string[];
  /** Every row's changed lines: surplus (removed, reduced) first, then
   *  shortfall (added, increased), each in row order. The plan-row order. */
  changedLines: OrderChangeLine[];
  /** True only when EVERY row gave at least one changed line. The card says
   *  sentences only then; otherwise a row's change would go unmentioned. */
  everyRowHasLines: boolean;
}

/**
 * Group a flat ORDER_CHANGED list into one entry per prod_order. Client-side on
 * purpose: `listIssues` already returns every row for the location, so a second
 * endpoint would only add a way for the two to disagree.
 *
 * Rows with no `prod_order_id` cannot be resolved (both mutations key off it),
 * so each becomes its own group and the card renders without actions rather
 * than being silently dropped.
 */
export function groupOrderChangeIssues(issues: ProductionIssue[]): OrderChangeGroup[] {
  const byKey = new Map<string, OrderChangeGroup>();

  for (const issue of issues) {
    const key = issue.prod_order_id ?? `issue:${issue.id}`;
    const view = readOrderChangeView(issue);
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        prodOrderId: issue.prod_order_id,
        issues: [],
        openIssueIds: [],
        changeKinds: [],
        under: 0,
        over: 0,
        evaluatedAt: null,
        allResolved: true,
        baseSkus: [],
        changedLines: [],
        everyRowHasLines: true,
      };
      byKey.set(key, group);
    }

    group.issues.push(issue);
    if (issue.status !== 'RESOLVED') {
      group.openIssueIds.push(issue.id);
      group.allResolved = false;
    }
    group.under += view.under;
    group.over += view.over;
    for (const kind of view.changeKinds) {
      if (!group.changeKinds.includes(kind)) group.changeKinds.push(kind);
    }
    if (
      view.evaluatedAt &&
      (!group.evaluatedAt || new Date(view.evaluatedAt) > new Date(group.evaluatedAt))
    ) {
      group.evaluatedAt = view.evaluatedAt;
    }
    if (issue.blocked_sku && !group.baseSkus.includes(issue.blocked_sku)) {
      group.baseSkus.push(issue.blocked_sku);
    }
    if (view.changedLines.length > 0) group.changedLines.push(...view.changedLines);
    else group.everyRowHasLines = false;
  }

  // Canonical kind order, so two cards with the same kinds read identically.
  for (const group of byKey.values()) {
    group.changeKinds.sort(
      (a, b) => ORDER_CHANGE_KINDS.indexOf(a) - ORDER_CHANGE_KINDS.indexOf(b),
    );
    // Surplus first, shortfall after: the order the resolver's plan rows use.
    group.changedLines = inPlanOrder(group.changedLines);
  }

  return [...byKey.values()];
}

/** Does any row in the group match the free-text query? Matched at GROUP level
 *  so a search never splits an order and lets "Accept as-is" resolve a subset. */
function groupMatchesQuery(group: OrderChangeGroup, query: string): boolean {
  return group.issues.some((issue) => {
    const name = issue.order_name ?? (issue.metadata?.order_name as string) ?? '';
    return `${name} ${issue.customer_display ?? ''} ${issue.blocked_sku ?? ''}`
      .toLowerCase()
      .includes(query);
  });
}

// ============================================================================
// BLU-695 §2b — bin-move banner, kept at LIST level (never inside the card).
//
// `OrderChangeResolution` lives inside a card that unmounts the moment its
// group leaves the OPEN list — which is exactly what happens right after a
// successful top-up (`invalidateOrderChangeSurfaces` refetches, the resolved
// group drops out). Holding the outcome here instead means the banner
// survives that unmount: it's a durable physical task ("go move this
// garment"), not a transient success receipt.
// ============================================================================

/** `BinMoveResult` plus the prodOrderId it's keyed/dismissed by — the
 *  list-level dedupe key `handleBinMove` pushes/replaces on. */
interface PendingBinMove extends BinMoveResult {
  prodOrderId: string;
}

function BinMoveBanner({
  move,
  onDismiss,
}: {
  move: BinMoveResult;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('production');
  return (
    <Alert variant="warning" role="status">
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          {move.binLabel !== null ? (
            <>
              <p>
                {t('issues.orderChanges.binMoveInstruction', {
                  order: move.orderLabel,
                  label: move.binLabel,
                })}
              </p>
              <p className="mt-1 font-mono font-semibold tabular-nums">{move.binLabel}</p>
            </>
          ) : (
            <p>{t('issues.orderChanges.binClaimFailed', { order: move.orderLabel })}</p>
          )}
        </div>
        <Button variant="outline" className="h-12 shrink-0" onClick={onDismiss}>
          {t('issues.orderChanges.binMoveDismiss')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

interface OrderChangeIssuesListProps {
  locationId: string;
}

/**
 * Public entry point. IssuesPage already wraps the whole board in
 * `IssuesErrorBoundary`; this tab adds its own because it is the only one that
 * renders free-form `prod_issues.metadata` JSON, so a malformed row should cost
 * one tab, not the entire Issues page. (`readOrderChangeView` already degrades
 * rather than throws — this is defence in depth.) Worth extending to the sibling
 * tabs in a follow-up.
 */
export function OrderChangeIssuesList(props: OrderChangeIssuesListProps) {
  return (
    <IssuesErrorBoundary>
      <OrderChangeIssuesListContent {...props} />
    </IssuesErrorBoundary>
  );
}

function OrderChangeIssuesListContent({ locationId }: OrderChangeIssuesListProps) {
  const { t } = useTranslation('production');
  // The same permission every order-change procedure checks server-side
  // (getOrderCoverage, topUpOrderCoverage, cancelSurplusUnits,
  // acceptOrderChanges). Without it the rows stay readable, but the resolver,
  // the Sheet and bulk accept are not offered: each would only return a 403.
  const { can } = useRole();
  const canResolveChanges = can('orders.resolve_changes');
  const [showResolved, setShowResolved] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // BLU-695 §2b — list-level, keyed by prodOrderId so it survives the card
  // that reported it unmounting (see the section comment above).
  const [pendingBinMoves, setPendingBinMoves] = useState<PendingBinMove[]>([]);
  const handleBinMove = useCallback((prodOrderId: string, result: BinMoveResult) => {
    setPendingBinMoves((prev) => [
      ...prev.filter((m) => m.prodOrderId !== prodOrderId),
      { prodOrderId, ...result },
    ]);
  }, []);
  const dismissBinMove = useCallback((prodOrderId: string) => {
    setPendingBinMoves((prev) => prev.filter((m) => m.prodOrderId !== prodOrderId));
  }, []);

  // Server-side pagination. The OPEN view drives total pages from the exact
  // open count (getIssueCounts — uncapped COUNT(*)); the resolved view (no
  // exact count) uses full-page detection. Mirrors PrintFileIssuesList.
  const { data: counts } = useIssueCounts(locationId);
  const openTotal = counts?.order_changed ?? 0;
  const openTotalPages = Math.max(1, Math.ceil(openTotal / FETCH_LIMIT));
  const safePage = showResolved ? page : Math.min(page, openTotalPages);

  const {
    data: issues,
    isLoading,
    isError,
    refetch,
  } = useProductionIssues(locationId, {
    issueType: 'ORDER_CHANGED',
    status: showResolved ? undefined : 'OPEN',
    limit: FETCH_LIMIT,
    offset: (safePage - 1) * FETCH_LIMIT,
  });

  // Group FIRST, then filter groups — filtering rows first would let a search
  // for one SKU hide its siblings and make "Accept as-is" clear a subset.
  // Search scans the current server page only (as before, when it scanned the
  // only page that existed).
  const isSearching = search.trim().length > 0;
  const paginatedGroups = useMemo(() => {
    const grouped = groupOrderChangeIssues(issues ?? []);
    const q = search.trim().toLowerCase();
    if (!q) return grouped;
    return grouped.filter((group) => groupMatchesQuery(group, q));
  }, [issues, search]);

  const hasNextPage = showResolved
    ? (issues?.length ?? 0) === FETCH_LIMIT
    : safePage < openTotalPages;
  const totalPages = showResolved ? undefined : openTotalPages;
  const showPager = safePage > 1 || hasNextPage;

  // ---- Bulk selection (PrintFileIssuesList mechanics, over GROUPS not rows) —
  // selectable = groups with something to dismiss (a resolvable order that
  // isn't already fully resolved), mirroring `canResolve` on the card itself.
  const selectableGroups = useMemo(
    () =>
      canResolveChanges
        ? paginatedGroups.filter((g) => !!g.prodOrderId && !g.allResolved)
        : [],
    [paginatedGroups, canResolveChanges]
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClickedIndexRef = useRef<number | null>(null);

  const handleToggleRow = useCallback(
    (index: number, key: string, shiftKey: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const last = lastClickedIndexRef.current;
        if (shiftKey && last !== null) {
          const [lo, hi] = last < index ? [last, index] : [index, last];
          for (let i = lo; i <= hi; i++) {
            const row = selectableGroups[i];
            if (row) next.add(row.key);
          }
        } else if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
      lastClickedIndexRef.current = index;
    },
    [selectableGroups]
  );

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    lastClickedIndexRef.current = null;
  }, []);

  const selectedGroups = useMemo(
    () => selectableGroups.filter((g) => selected.has(g.key)),
    [selectableGroups, selected]
  );

  // Dismiss = bulk "Accept as-is" (per selected order, sequential). Deliberately
  // TEXT-ONLY in the toolbar (Brett: dismissing must not look more attractive
  // than fixing) — see the render below.
  const [bulkDismissOpen, setBulkDismissOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [isBulkDismissing, setIsBulkDismissing] = useState(false);
  /** Sticky, in-surface partial-failure text — a toast alone vanishes under the fold. */
  const [bulkFailure, setBulkFailure] = useState<string | null>(null);
  const bulkReasonId = useId();
  const acceptOrderChanges = useAcceptOrderChanges();
  const canBulkDismiss = bulkReason.trim().length > 0;

  const handleBulkDismissConfirm = useCallback(async () => {
    const reason = bulkReason.trim();
    if (!reason || selectedGroups.length === 0 || isBulkDismissing) return;
    setIsBulkDismissing(true);
    setBulkFailure(null);

    // Sequential, per-order try/catch (cancelSurplusUnits / Cancelled-tab bulk
    // pattern): a throw at order k must not report orders 1..k-1 as "nothing
    // was changed".
    let done = 0;
    const failed: Array<{ prodOrderId: string; error: string }> = [];
    for (const group of selectedGroups) {
      if (!group.prodOrderId) continue;
      try {
        // acceptOrderChanges RESOLVES with { done, failed } — a row-level failure does
        // NOT throw (mirrors OrderChangeResolution's handleAccept). An order only counts
        // as dismissed when every one of its rows actually cleared.
        const result = await acceptOrderChanges.mutateAsync({ prod_order_id: group.prodOrderId, notes: reason });
        if (result.failed.length === 0) {
          done++;
        } else {
          failed.push({ prodOrderId: group.prodOrderId, error: result.failed[0]?.error ?? '' });
        }
      } catch (err) {
        failed.push({ prodOrderId: group.prodOrderId, error: getUserMessage(err) });
      }
    }

    setIsBulkDismissing(false);
    setBulkDismissOpen(false);
    clearSelection();
    setBulkReason('');
    if (failed.length === 0) {
      toast.success(t('issues.orderChanges.bulk.done', { count: done }));
    } else {
      const total = done + failed.length;
      const message = t('issues.orderChanges.bulk.partial', { done, total });
      setBulkFailure(message);
      toast.error(t('issues.orderChanges.bulk.partialTitle'), {
        description: message,
        duration: Infinity,
      });
    }
  }, [bulkReason, selectedGroups, isBulkDismissing, acceptOrderChanges, clearSelection, t]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" aria-hidden="true" />
        <p className="mt-4 text-lg font-medium">
          {t('issues.orderChanges.failedToLoad')}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-2 text-sm text-primary hover:underline"
        >
          {t('issues.orderChanges.retry')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* BLU-695 §2b — durable physical-task banners, list-level so they
          outlive the card/order that reported them (see the section comment
          above `PendingBinMove`). */}
      {pendingBinMoves.length > 0 && (
        <div className="mb-4 space-y-2">
          {pendingBinMoves.map((move) => (
            <BinMoveBanner
              key={move.prodOrderId}
              move={move}
              onDismiss={() => dismissBinMove(move.prodOrderId)}
            />
          ))}
        </div>
      )}

      {/* Tab heading */}
      <div className="mb-4 space-y-0.5">
        <h2 className="text-sm font-semibold">{t('issues.orderChanges.listHeading')}</h2>
        <p className="max-w-prose text-sm text-muted-foreground">
          {t('issues.orderChanges.listDesc')}
        </p>
      </div>

      {/* Controls — replaced by the selection toolbar when anything is selected */}
      {selected.size > 0 ? (
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/30 px-3 py-2"
          role="region"
          aria-label={t('issues.orderChanges.bulk.toolbarLabel')}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {t('issues.orderChanges.bulk.selectedCount', { count: selected.size })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1 text-xs text-muted-foreground"
              onClick={clearSelection}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              {t('issues.orderChanges.bulk.clear')}
            </Button>
          </div>
          {/* TEXT-ONLY, no icon, no background — dismissing must not look more
              attractive than fixing (Brett). h-12 still — this IS the action lane. */}
          <Button
            variant="ghost"
            className="h-12 text-muted-foreground hover:text-foreground"
            onClick={() => {
              // Pre-fill, editable — never overwrite an in-progress edit.
              setBulkReason((prev) =>
                prev.trim() === '' ? t('issues.orderChanges.bulk.defaultReason') : prev
              );
              setBulkFailure(null);
              setBulkDismissOpen(true);
            }}
          >
            {t('issues.orderChanges.bulk.dismiss', { count: selected.size })}
          </Button>
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <IssueSearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder={t('issues.filters.searchPlaceholder')}
            ariaLabel={t('issues.filters.searchAriaLabel')}
            clearLabel={t('issues.filters.clearSearch')}
          />
          <div className="flex items-center gap-2">
            <Switch
              id="show-resolved-order-changes"
              checked={showResolved}
              onCheckedChange={(checked) => {
                setShowResolved(checked);
                setPage(1);
              }}
            />
            <Label
              htmlFor="show-resolved-order-changes"
              className="text-sm text-muted-foreground"
            >
              {t('issues.orderChanges.showResolved')}
            </Label>
          </div>
          <p className="ml-auto text-sm text-muted-foreground">
            {isSearching
              ? t('issues.searchMatchCount', { count: paginatedGroups.length })
              : t('issues.orderChanges.orderCount', { count: paginatedGroups.length })}
          </p>
        </div>
      )}

      {bulkFailure && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {bulkFailure}
        </p>
      )}

      {/* Order cards */}
      {paginatedGroups.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={FileEdit}
              title={
                isSearching
                  ? t('issues.orderChanges.noResults')
                  : t('issues.orderChanges.noIssues')
              }
              description={
                isSearching
                  ? t('issues.orderChanges.noResultsDesc')
                  : showResolved
                    ? t('issues.orderChanges.noneRecorded')
                    : t('issues.orderChanges.allMatch')
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {paginatedGroups.map((group) => {
            const selectableIndex = selectableGroups.findIndex((g) => g.key === group.key);
            return (
              <OrderChangeCard
                key={group.key}
                group={group}
                canResolveChanges={canResolveChanges}
                selectable={selectableIndex !== -1}
                selected={selected.has(group.key)}
                onToggleSelect={(shiftKey) => handleToggleRow(selectableIndex, group.key, shiftKey)}
                onBinMove={handleBinMove}
              />
            );
          })}
        </div>
      )}

      {/* Pagination (server-side) */}
      {showPager && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => {
              setPage(Math.max(1, safePage - 1));
              clearSelection();
            }}
          >
            {t('issues.pagination.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {totalPages == null
              ? t('issues.pagination.pageCurrent', { current: safePage })
              : t('issues.pagination.pageOf', { current: safePage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNextPage}
            onClick={() => {
              setPage(safePage + 1);
              clearSelection();
            }}
          >
            {t('issues.pagination.next')}
          </Button>
        </div>
      )}

      {/* Bulk dismiss confirm — requires a typed reason, pre-filled + editable.
          Plain Button (not AlertDialogAction) so the dialog stays open through
          the sequential mutateAsync loop, matching OrderChangeResolution's own
          confirm dialogs. */}
      <AlertDialog
        open={bulkDismissOpen}
        onOpenChange={(open) => {
          if (!open && !isBulkDismissing) setBulkDismissOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('issues.orderChanges.bulk.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('issues.orderChanges.bulk.confirmBody', { count: selected.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor={bulkReasonId} className="text-sm font-medium">
              {t('issues.orderChanges.acceptReasonLabel')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Textarea
              id={bulkReasonId}
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              maxLength={500}
              rows={2}
              className="min-h-12 max-w-prose"
              placeholder={t('issues.orderChanges.acceptReasonPlaceholder')}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12" disabled={isBulkDismissing}>
              {t('issues.orderChanges.back')}
            </AlertDialogCancel>
            <Button
              variant="ghost"
              className="h-12 gap-1.5"
              disabled={isBulkDismissing || !canBulkDismiss}
              onClick={handleBulkDismissConfirm}
            >
              {isBulkDismissing && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('issues.orderChanges.bulk.dismiss', { count: selected.size })}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// OrderChangeCard — one order, one mutation target
// ============================================================================

interface OrderChangeCardProps {
  group: OrderChangeGroup;
  /** Caller holds `orders.resolve_changes`. False = a read-only card: no Review
   *  button, no inline resolver, no Sheet. */
  canResolveChanges: boolean;
  /** Bulk-selection checkbox — present only while the list's selection toolbar
   *  is active. Omit to render exactly as before. */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (shiftKey: boolean) => void;
  /** BLU-695 §2b — reports a bin claim/failure up to the LIST's durable,
   *  list-level banner (`pendingBinMoves`). */
  onBinMove: (prodOrderId: string, result: BinMoveResult) => void;
}

function OrderChangeCard({
  group,
  canResolveChanges,
  selectable = false,
  selected = false,
  onToggleSelect,
  onBinMove,
}: OrderChangeCardProps) {
  const { t } = useTranslation('production');
  const { organization } = useOrganization();
  // Cross-org provenance: a 3PL owner reading a client's drifted order.
  const { isCrossOrg, labelFor } = useClientOrgLabels();

  const [expanded, setExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const headIssue = group.issues[0];
  const { changeKinds, under, over, evaluatedAt, baseSkus, allResolved } = group;
  // One sentence per changed line, when every row of the order can give one.
  // A row with no per-line data keeps the base SKU line and the kind badges.
  const translate: Translate = (key, params) => t(key, params);
  const sentenceLines = group.everyRowHasLines ? group.changedLines : [];
  const showSentences = sentenceLines.length > 0;
  const metadata = headIssue?.metadata;
  const orderLabel =
    headIssue?.order_name ??
    (metadata?.order_name as string) ??
    t('issues.orderChanges.unknownOrder');

  // Same validated admin-URL builder the other tabs use; renders plain text when
  // the store slug or the Shopify order id is missing.
  const shopifyOrderId =
    headIssue?.shopify_order_id ?? (metadata?.shopify_order_id as number | undefined);
  const shopifyUrl = getShopifyAdminOrderUrl(organization?.shopify_domain, shopifyOrderId);

  // A resolved-only group, or one with no prod_order link, has nothing to act
  // on; and a caller without the permission has nothing they are allowed to do.
  const canResolve = canResolveChanges && !!group.prodOrderId && !allResolved;
  // Where a permitted caller would see the Review button, a read-only caller is
  // told who can act instead of being left with a card and no way forward.
  const showWhoCanFix = !canResolveChanges && !!group.prodOrderId && !allResolved;
  const presentation = resolutionPresentation(baseSkus.length);
  const panelId = `order-change-plan-${group.key}`;

  const handleBinMove = (result: BinMoveResult) => {
    if (!group.prodOrderId) return;
    onBinMove(group.prodOrderId, result);
  };

  return (
    <>
      <Card
        className={cn(
          selected && 'ring-1 ring-threadheads-purple/40',
          // Selection outranks the resolved dimming — never both.
          !selected && allResolved && 'opacity-60'
        )}
      >
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            {/* Selection checkbox — only when the list's selection toolbar is active */}
            {selectable && onToggleSelect && (
              <div
                className="flex h-6 shrink-0 items-center"
                // Capture shiftKey from the underlying click before the Checkbox
                // fires its change handler.
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect(e.shiftKey);
                }}
              >
                <Checkbox
                  checked={selected}
                  aria-label={t('issues.orderChanges.bulk.selectRow', { order: orderLabel })}
                  className="pointer-events-none"
                />
              </div>
            )}

            {/* Issue-type icon — orange, matching issueTypeConfig.ORDER_CHANGED. */}
            <div className="flex-shrink-0 rounded-md bg-orange-50 p-1.5 text-orange-600">
              <FileEdit className="h-4 w-4" aria-hidden="true" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {shopifyUrl ? (
                      <a
                        href={shopifyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('issues.orderChanges.viewOnShopifyAria', {
                          order: orderLabel,
                        })}
                        className="inline-flex items-center gap-1 rounded text-sm font-semibold text-threadheads-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      >
                        {orderLabel}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    ) : (
                      <span className="text-sm font-semibold">{orderLabel}</span>
                    )}
                    {headIssue?.customer_display && (
                      <span className="text-sm text-muted-foreground">
                        {headIssue.customer_display}
                      </span>
                    )}
                    {headIssue && isCrossOrg(headIssue.organization_id) && (
                      <ClientOrgChip label={labelFor(headIssue.organization_id)} />
                    )}
                    {allResolved && (
                      <Badge variant="success" className="text-xs">
                        {t('issues.orderChanges.resolvedBadge')}
                      </Badge>
                    )}
                  </div>

                  {/* Base garment SKUs — labelled, because they are NOT the
                      design-bearing shopify_sku the print surfaces show. Rows
                      with no per-line data only. */}
                  {!showSentences && baseSkus.length > 0 && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('issues.orderChanges.baseSku', { count: baseSkus.length })}{' '}
                      <span className="font-mono text-foreground">
                        {baseSkus.slice(0, MAX_SKUS_IN_HEADER).join(', ')}
                      </span>
                      {baseSkus.length > MAX_SKUS_IN_HEADER && (
                        <span className="ml-1">
                          {t('issues.orderChanges.moreSkus', {
                            count: baseSkus.length - MAX_SKUS_IN_HEADER,
                          })}
                        </span>
                      )}
                    </p>
                  )}
                </div>

                {evaluatedAt && (
                  <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                    {t('issues.orderChanges.detected', {
                      time: formatDistanceToNow(new Date(evaluatedAt), { addSuffix: true }),
                    })}
                  </span>
                )}
              </div>

              {/* What changed, by garment name. The base SKU follows a named
                  line as secondary text; with no stored name the SKU is the
                  subject of the sentence, so it is not repeated. Below the
                  header row, so a sentence gets the card's full width and not
                  what the detected time leaves of it. */}
              {showSentences && (
                <div className="mt-1">
                  <span className="sr-only">{t('issues.orderChanges.whatChanged')}</span>
                  <ul className="space-y-0.5">
                    {sentenceLines.slice(0, MAX_SKUS_IN_HEADER).map((changed) => {
                      const ChangeIcon = changed.over > 0 ? MinusCircle : PlusCircle;
                      const named = garmentLabel(changed.display) !== null;
                      return (
                        <li key={changed.key} className="flex items-start gap-1.5 text-sm">
                          <span className="flex h-5 shrink-0 items-center">
                            <ChangeIcon
                              className="h-3.5 w-3.5 text-muted-foreground"
                              aria-hidden="true"
                            />
                          </span>
                          <span className="min-w-0 break-words">
                            <span>{changeSentence(changed, translate)}</span>
                            {named && changed.sku && (
                              <>
                                {' '}
                                <span aria-hidden="true" className="text-muted-foreground">
                                  ·
                                </span>{' '}
                                <span className="font-mono text-xs text-muted-foreground">
                                  {changed.sku}
                                </span>
                              </>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {sentenceLines.length > MAX_SKUS_IN_HEADER && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t('issues.orderChanges.moreSkus', {
                        count: sentenceLines.length - MAX_SKUS_IN_HEADER,
                      })}
                    </p>
                  )}
                </div>
              )}

              {/* What changed + by how much + the way in. */}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {!showSentences && changeKinds.length > 0 && (
                  <>
                    <span className="sr-only">{t('issues.orderChanges.whatChanged')}</span>
                    {changeKinds.map((kind) => (
                      <Badge key={kind} variant="outline" className="text-xs">
                        {t(`issues.orderChanges.kind.${kind}`)}
                      </Badge>
                    ))}
                  </>
                )}
                {under > 0 && (
                  <Badge variant="warning" className="text-xs tabular-nums">
                    <ArrowUp className="h-3 w-3" aria-hidden="true" />
                    {t('issues.orderChanges.needsMore', { count: under })}
                  </Badge>
                )}
                {over > 0 && (
                  <Badge variant="info" className="text-xs tabular-nums">
                    <ArrowDown className="h-3 w-3" aria-hidden="true" />
                    {t('issues.orderChanges.surplus', { count: over })}
                  </Badge>
                )}

                {canResolve && (
                  <Button
                    variant="outline"
                    className="ml-auto h-12 gap-1.5"
                    aria-expanded={presentation === 'inline' ? expanded : undefined}
                    aria-controls={presentation === 'inline' && expanded ? panelId : undefined}
                    onClick={() =>
                      presentation === 'inline' ? setExpanded((v) => !v) : setSheetOpen(true)
                    }
                  >
                    {presentation === 'inline' ? (
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 transition-transform duration-200 motion-reduce:transition-none',
                          expanded && 'rotate-180',
                        )}
                        aria-hidden="true"
                      />
                    ) : (
                      <PanelRightOpen className="h-4 w-4" aria-hidden="true" />
                    )}
                    {presentation === 'inline' && expanded
                      ? t('issues.orderChanges.hideChanges')
                      : t('issues.orderChanges.reviewChanges')}
                  </Button>
                )}
                {showWhoCanFix && (
                  <p className="ml-auto text-xs text-muted-foreground">
                    {t('issues.orderChanges.fixedByCoordinatorOrCx')}
                  </p>
                )}
              </div>

              {/* Inline path (1 to 2 changed SKUs): the plan expands in place. */}
              {canResolve && presentation === 'inline' && expanded && group.prodOrderId && (
                <div id={panelId} className="mt-4 border-t pt-4">
                  <OrderChangeResolution
                    prodOrderId={group.prodOrderId}
                    orderLabel={orderLabel}
                    openIssueIds={group.openIssueIds}
                    confirmMode="dialog"
                    onResolved={() => setExpanded(false)}
                    onBinMove={handleBinMove}
                  />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overflow path (3+ changed SKUs): the same resolver in a side panel. */}
      {canResolve && presentation === 'sheet' && group.prodOrderId && (
        <OrderChangeSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          prodOrderId={group.prodOrderId}
          openIssueIds={group.openIssueIds}
          orderLabel={orderLabel}
          customerDisplay={headIssue?.customer_display}
          onBinMove={handleBinMove}
        />
      )}
    </>
  );
}
